import type { ModifierState } from "@/domain/profile-resolver/index";
import type { Keyboard as KeyboardModel, RenderKeycap } from "@/domain/types";
import type { LayoutGroup } from "@/hardware/keyboard-layouts/picker";
import type { ImportedKeyboard } from "@/hardware/keyboards/keyboard-import";
import { KeyboardSelect } from "@/render/components/KeyboardSelect";
import { LayoutPick } from "@/render/components/LayoutPick";
import { PlusNote } from "@/render/components/PlusNote";
import { SetupPicker, type SetupPickerProps } from "@/render/components/SetupPicker";
import { Surfaces } from "@/render/components/Surfaces";
import type { ChipReservation, RenderZone } from "@/render/mouse-map/compose";

/**
 * One compare side, resolved by the page: its Profile / Keyboard / Layout controls plus its finished
 * render bundle. A and B carry the SAME shape; the B side's controls just offer the "Same as A"
 * sentinel (keyboard.sameAs / layout.sameAs) and a "Layout B" aria-label. The page builds both, so this
 * view never branches on which side it is rendering.
 */
export interface CompareSide {
  tag: "A" | "B";
  setup: SetupPickerProps;
  keyboard: {
    value: string;
    onChange: (id: string) => void;
    /** B-side "Same as A: <board>" leading option; absent on A. */
    sameAs?: { label: string } | null;
    disabled?: boolean;
  };
  layout: {
    groups: LayoutGroup[];
    value: string;
    onChange: (id: string) => void;
    onRename?: (name: string) => void;
    /** B-side "Same as A: <layout>" / "Default: <layout>" leading option; absent on A. */
    sameAs?: { label: string } | null;
    disabled?: boolean;
    /** A keeps the default "Keyboard Layout"; B passes "Layout B". */
    ariaLabel?: string;
  };
  board: KeyboardModel;
  keycaps: RenderKeycap[];
  zones: RenderZone[];
  lit: string[] | null;
  comboHasExact: boolean;
  /** This side carries a Plus source that is not rendering remapped: annotate the cause. */
  plusNote?: "no-neighbor" | "board-layer" | null;
}

/** One compare column: the side's header card (Profile / Keyboard / Layout) over its board+mouse. */
function CompareColumn({
  side,
  imported,
  unit,
  mods,
  reserve,
  pressedCodes,
  onPress,
  iconFor,
  kbWrapStyle,
}: {
  side: CompareSide;
  imported: Record<string, ImportedKeyboard>;
  unit: number;
  mods: ModifierState;
  reserve: ChipReservation;
  pressedCodes: string[] | null;
  onPress: (code: string, legend: string) => void;
  iconFor?: (cmd: string | undefined) => string | null;
  kbWrapStyle: React.CSSProperties;
}) {
  return (
    <div className="cmp-col">
      {/* each side carries its OWN Profile, Keyboard, and Layout (per-side boards); the card
          left-aligns to the board's plate below it */}
      <div className="cmp-hd">
        <span className={side.tag === "B" ? "cmp-tag b" : "cmp-tag"}>{side.tag}</span>
        <span className="ctl-lbl">Profile</span>
        <SetupPicker {...side.setup} ariaLabel={`Profile ${side.tag}`} />
        <span className="ctl-lbl">Keyboard</span>
        <span className="ctl-select">
          <KeyboardSelect
            ariaLabel={`Keyboard ${side.tag}`}
            value={side.keyboard.value}
            onChange={side.keyboard.onChange}
            imported={imported}
            sameAs={side.keyboard.sameAs}
            disabled={side.keyboard.disabled}
          />
        </span>
        <span className="ctl-lbl">Layout</span>
        <span className="ctl-select">
          <LayoutPick
            ariaLabel={side.layout.ariaLabel}
            groups={side.layout.groups}
            value={side.layout.value}
            onChange={side.layout.onChange}
            onRename={side.layout.onRename}
            sameAs={side.layout.sameAs}
            disabled={side.layout.disabled}
          />
        </span>
      </div>
      {side.plusNote ? <PlusNote reason={side.plusNote} /> : null}
      <div className="kb-wrap" style={kbWrapStyle}>
        <Surfaces
          board={side.board}
          keycaps={side.keycaps}
          zones={side.zones}
          litCodes={side.lit}
          dimCategories={null}
          dimFallback={side.comboHasExact}
          unit={unit}
          mods={mods}
          reserve={reserve}
          pressedCodes={pressedCodes}
          onPress={onPress}
          iconFor={iconFor}
        />
      </div>
    </div>
  );
}

/**
 * The compare canvas: two stacked columns (A over B), each its own Profile / Keyboard / Layout header
 * card over its own board+mouse surfaces. The page resolves each side into a CompareSide; this view
 * renders the same column twice. Both surfaces share the one key unit so neither plate reflows.
 */
export function CompareView({
  wrapRef,
  unit,
  platePad,
  mods,
  reserve,
  pressedCodes,
  onPress,
  imported,
  iconFor,
  a,
  b,
}: {
  wrapRef: React.Ref<HTMLDivElement>;
  unit: number;
  platePad: number;
  mods: ModifierState;
  reserve: ChipReservation;
  pressedCodes: string[] | null;
  onPress: (code: string, legend: string) => void;
  imported: Record<string, ImportedKeyboard>;
  iconFor?: (cmd: string | undefined) => string | null;
  a: CompareSide;
  b: CompareSide;
}) {
  const kbWrapStyle = {
    gap: 0.5 * unit,
    paddingLeft: platePad,
    paddingTop: platePad,
    paddingBottom: platePad,
  };
  const shared = { imported, unit, mods, reserve, pressedCodes, onPress, iconFor, kbWrapStyle };
  return (
    <div ref={wrapRef} className="cmp">
      <CompareColumn side={a} {...shared} />
      <CompareColumn side={b} {...shared} />
    </div>
  );
}
